import {
	Box,
	Typography,
	Autocomplete,
	TextField,
	IconButton,
} from '@mui/material';
import React, { useEffect } from 'react';
import { CANtrackComment } from '../../types/comment';

import {
	CommentFormState,
	CommentFormStateErrors,
} from '../../hooks/useCommentEditorForm';
import useCases from '../../hooks/useCases';
import FloatingEmployeeSelector from '../floatingAutocomplete/FloatingEmployeeSelector';
import FloatingCompanySelector from '../floatingAutocomplete/FloatingCompanySelector';
import useActivities from '../../hooks/useActivities';
import ChangeCircleOutlinedIcon from '@mui/icons-material/ChangeCircleOutlined';
import CaseSearchAutocomplete from './CaseSearchAutocomplete';

const StyledCGPD = ({ formState }: { formState: CommentFormState }) => (
	<Typography variant="caption" color={'textSecondary'} textAlign={'left'}>
		CGPD:{' '}
		{formState.selectedCase ? (
			<>
				<Typography
					component="span"
					variant="caption"
					color={'primary'}
				>
					{formState.selectedCase.customerName}
				</Typography>
				/
				<Typography
					component="span"
					variant="caption"
					color={'success.light'}
				>
					{formState.selectedCase.gpd.group.title}
				</Typography>
				/
				<Typography
					component="span"
					variant="caption"
					color={'warning.light'}
				>
					{formState.selectedCase.gpd.project.title}
				</Typography>
				/
				<Typography
					component="span"
					variant="caption"
					color={'error.light'}
				>
					{formState.selectedCase.gpd.deliverable.title}
				</Typography>
				/
			</>
		) : (
			<Typography component="span" variant="caption">
				No case selected
			</Typography>
		)}
	</Typography>
);

const ActivityGPDA: React.FC<{
	initialComment?: CANtrackComment;
	formState: CommentFormState;
	errors: CommentFormStateErrors;
	handleChange: <T extends keyof CommentFormState>(
		field: T,
		value: CommentFormState[T],
		validate?: boolean,
	) => void;
	lockedCase?: boolean;
}> = (props) => {
	const { formState, errors, handleChange, initialComment, lockedCase } = props;
	const {
		loadingCases,
		cases,
		totalCases,
		isSearchingUsingCaseID,
		appliedFilters,
		pinnedCases,
		handleSearchChange,
		setAppliedFilter,
		updateEmployeeInFilter,
		updateCompanyInFilter,
		updatePinnedCases,
	} = useCases();
	const { activities, loadingActivities, setActivities, fetchActivities } =
		useActivities();

	useEffect(() => {
		if (formState.selectedCase) {
			fetchActivities(formState.selectedCase.gpd.deliverable.id);
			if (initialComment) {
				if (initialComment.caseID !== formState.selectedCase.id) {
					setActivities([]);
					handleChange('selectedActivity', null, false);
				}
			}
		}
	}, [formState.selectedCase]);

	const caseSelector = (
		<Box>
			<CaseSearchAutocomplete
				cases={cases}
				totalCases={totalCases}
				pinnedCases={pinnedCases}
				loadingCases={loadingCases}
				errors={errors}
				handleChange={handleChange}
				formState={formState}
				handleSearchChange={handleSearchChange}
				setAppliedFilter={setAppliedFilter}
				updatePinnedCases={updatePinnedCases}
				disabled={lockedCase}
			/>
			{initialComment &&
				initialComment?.caseID !== formState.selectedCase?.id && (
					<Typography
						color={'warning.main'}
						textAlign={'left'}
						variant="caption"
					>
						Warning: You are reassigning this activity from "
						<span style={{ fontStyle: 'italic' }}>
							{initialComment?.caseTitle}
						</span>
						".
					</Typography>
				)}
		</Box>
	);

	const cgpdAndBillable = (
		<Box display={'flex'} flexDirection={'column'}>
			<StyledCGPD formState={formState} />
			<Typography variant="caption" color={'textSecondary'}>
				This activity is{' '}
				<Typography
					variant="caption"
					color={formState.billable ? 'primary' : 'error.light'}
				>
					{formState.billable ? 'Billable' : 'Non-billable'}
				</Typography>
				<IconButton
					onClick={() =>
						handleChange('billable', !formState.billable)
					}
				>
					<ChangeCircleOutlinedIcon fontSize="small" />
				</IconButton>
			</Typography>
		</Box>
	);

	const activitySelector = (
		<Box>
			<Autocomplete
				options={activities}
				loading={loadingActivities}
				getOptionLabel={(option) => option.Title}
				value={formState.selectedActivity}
				onChange={(e, newValue) => {
					handleChange('selectedActivity', newValue);
				}}
				isOptionEqualToValue={(option, value) =>
					option.Id === value.Id
				}
				renderInput={(params) => (
					<TextField
						{...params}
						label="Activity"
						variant="outlined"
						error={!!errors.selectedActivity}
						helperText={errors.selectedActivity}
					/>
				)}
			/>
		</Box>
	);

	return (
		<Box
			sx={{
				display: 'grid',
				gridTemplateColumns: {
					xs: '1fr',
					md: '1fr 1fr',
				},
				gridTemplateRows: {
					xs: 'auto auto',
					md: '1fr 1fr',
				},
				gap: 1,
			}}
		>
			{!lockedCase && (
				<Box
					order={{ xs: 1, md: 1 }}
					display={'flex'}
					flexDirection={'row'}
					gap={1}
				>
					<Box
						display={!isSearchingUsingCaseID ? 'flex' : 'none'} // Hide the filter bar when searching by case ID. Hiding the element to preserve the state inside
						flexDirection={'row'}
						gap={1}
					>
						<FloatingEmployeeSelector
							updateEmployeeInFilter={updateEmployeeInFilter}
						/>
						<FloatingCompanySelector
							updateCompanyInFilter={updateCompanyInFilter}
						/>
					</Box>
					{isSearchingUsingCaseID && (
						<Box>
							<Typography variant="caption">
								Searching for case with ID{' '}
								{appliedFilters.SearchKeyword} ...
							</Typography>
						</Box>
					)}
				</Box>
			)}
			<Box order={{ xs: 3, md: 2 }}>
				{cgpdAndBillable}
			</Box>
			<Box order={{ xs: 2, md: 3 }}>
				{lockedCase ? (
					<TextField
						label="Case"
						value={formState.selectedCase?.title || ''}
						disabled
						fullWidth
						helperText="Locked to current case"
					/>
				) : (
					caseSelector
				)}
			</Box>
			<Box order={{ xs: 4, md: 4 }}>
				{activitySelector}
			</Box>
		</Box>
	);
};

export default ActivityGPDA;
